import { DatePipe } from "@angular/common";
import { Component, computed, inject, linkedSignal, model, resource } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { Router, RouterLink } from "@angular/router";
import { AccessControlService } from "@dis/auth";
import { BarcodeScannerComponent } from "@dis/shared";
import { headerSearchComponentUsageRef, headerSearchRef } from "@jtms/default-connector";
import { executeServerQuery } from "../../tables/shared/execute-server-query";
import { computedWith } from "ngx-signal-operators";

import { allFieldsSearchVars } from "../../tables/shared/search-pattern";
import { searchGroups, searchWords, shipmentNumbersIn, type SearchRow } from "./header-search-groups";
import { HighlightWordsPipe } from "./highlight-words.pipe";

// Hosts whose links (e.g. shipment label QR codes) are routed in-app instead of searched.
const JTMS_ORIGINS = ["https://jtms.drillingllc.com", "https://jtms-dev.web.app"];

let nextId = 0;

@Component({
  selector: "jtms-search-bar-results",
  imports: [
    DatePipe,
    HighlightWordsPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    RouterLink,
  ],
  templateUrl: "./search-bar-results.html",
  styleUrl: "./search-bar-results.css",
})
export class SearchBarResults {
  readonly searchString = model<string>("");

  readonly #access = inject(AccessControlService);
  readonly #dialogRef = inject(MatDialogRef<SearchBarResults>, { optional: true });
  readonly #dialog = inject(MatDialog);
  readonly #router = inject(Router);

  protected readonly listId = `header-search-results-${nextId++}`;

  readonly #debounced = computedWith(this.searchString).debounce(250);
  protected readonly words = computed(() => searchWords(this.#debounced()));

  readonly #results = resource({
    params: () => {
      const text = this.#debounced().trim();
      if (!text) return undefined;
      return {
        ...allFieldsSearchVars(text),
        shipmentNumbers: shipmentNumbersIn(searchWords(text)),
        // Only admins see VOID records; the query's @auth refuses hideVoid: false for anyone else.
        hideVoid: !this.#access.isSystemUser(),
        ...this.#access.viewScope(),
      };
    },
    loader: async ({ params }) => (await executeServerQuery(headerSearchRef(params))).data,
  });

  readonly #usage = resource({
    params: () => {
      const components = this.#results.hasValue() ? this.#results.value().components : [];
      return components.length ? { componentIds: components.map((component) => component.id), ...this.#access.viewScope() } : undefined;
    },
    loader: async ({ params }) => (await executeServerQuery(headerSearchComponentUsageRef(params))).data,
  });

  protected readonly isLoading = computed(() => this.#results.isLoading() || this.#usage.isLoading());

  protected readonly groups = computed(() => {
    if (!this.#results.hasValue()) return [];
    return searchGroups(this.#results.value(), this.#usage.hasValue() ? this.#usage.value() : null, !this.#access.isSystemUser());
  });

  readonly #rows = computed(() => this.groups().flatMap((group) => group.rows));
  protected readonly rowIndex = computed(() => new Map(this.#rows().map((row, index) => [row.key, index])));
  protected readonly activeIndex = linkedSignal({ source: this.#rows, computation: () => 0 });
  protected readonly activeOptionId = computed(() => (this.#rows().length ? this.optionId(this.activeIndex()) : null));

  protected optionId(index: number): string {
    return `${this.listId}-option-${index}`;
  }

  protected onKeydown(event: KeyboardEvent): void {
    const count = this.#rows().length;
    if (!count) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      this.activeIndex.update((index) => (index + step + count) % count);
      document.getElementById(this.optionId(this.activeIndex()))?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter") {
      event.preventDefault();
      this.open(this.#rows()[this.activeIndex()]);
    }
  }

  protected open(row: SearchRow | undefined): void {
    if (!row) return;
    void this.#router.navigate(row.link, { queryParams: row.queryParams });
    this.closeModal();
  }

  closeModal(): void {
    this.#dialogRef?.close();
  }

  openScannerDialog(): void {
    this.#dialog
      .open<BarcodeScannerComponent, unknown, string | false>(BarcodeScannerComponent)
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        const path = jtmsPathFromScan(result);
        if (path) {
          void this.#router.navigateByUrl(path);
          this.closeModal();
        } else {
          this.searchString.set(result);
        }
      });
  }
}

/** The in-app path for a scanned JTMS link, or null when the scan isn't one (e.g. a serial number barcode). */
function jtmsPathFromScan(scan: string): string | null {
  let url: URL;
  try {
    url = new URL(scan.trim());
  } catch {
    return null;
  }
  const isJtms = url.origin === window.location.origin || JTMS_ORIGINS.includes(url.origin);
  return isJtms ? `${url.pathname}${url.search}${url.hash}` : null;
}
